/** Display labels, ordering and colours for shift cover; the rules live in _shared/shiftExceptions.ts. */
import { differenceInCalendarDays, format, startOfDay } from "date-fns"

import { toneClasses, type Tone } from "@/lib/tone"
import { cn } from "@/lib/utils"
import type {
  CoveredRoute,
  ShiftException,
  ShiftExceptionSeverity,
  ShiftExceptionType,
} from "@/types/collections"

/** Route-row flag; deliberately coarse, the specifics are in the row's `detail` tooltip. */
export const COVER_LABEL: Record<ShiftExceptionType, string> = {
  missing_shift: "No Shift",
  // A shift exists and is staffed, but the route needs another crew member.
  understaffed_crew: "Crew Short",
  unfilled_shift: "Shift Unfilled",
  duplicate_shift: "Duplicate Shift",
  start_time_mismatch: "Details Differ",
  end_time_mismatch: "Details Differ",
  driver_mismatch: "Details Differ",
  vehicle_mismatch: "Details Differ",
  vehicle_class_mismatch: "Details Differ",
  depot_mismatch: "Details Differ",
  shift_without_route: "No Route",
}

export const COVER_BETA_NOTE =
  "Shift cover is in beta. It matches shifts to routes by the route code in the shift note, so a wrong flag is possible; say so if you see one."

/** Worst first. */
export const SEVERITY_ORDER: ShiftExceptionSeverity[] = ["unstaffed", "drift", "orphan"]

export const SEVERITY_LABEL: Record<ShiftExceptionSeverity, string> = {
  unstaffed: "No Cover",
  drift: "Details Differ",
  orphan: "Shifts Without a Route",
}

export const SEVERITY_ACTION: Record<ShiftExceptionSeverity, string> = {
  unstaffed: "Book a shift, or fill the one booked",
  drift: "Edit the shift or the route until they agree",
  orphan: "Check the note; may be other work",
}

export const SEVERITY_TONE: Record<ShiftExceptionSeverity, Tone> = {
  unstaffed: "danger",
  drift: "warning",
  orphan: "neutral",
}

export function coverTextClass(severity: ShiftExceptionSeverity): string {
  return cn(
    toneClasses(SEVERITY_TONE[severity]).text,
    severity === "unstaffed" ? "font-semibold" : "font-medium",
  )
}

export function worstException(list: ShiftException[]): ShiftException {
  return [...list].sort(
    (a, b) => SEVERITY_ORDER.indexOf(a.severity) - SEVERITY_ORDER.indexOf(b.severity),
  )[0]
}

/** Ascending sort key: severities first, then covered routes, then routes the check said nothing about. */
export function coverRank(
  list: ShiftException[] | undefined,
  covered: CoveredRoute[] | undefined,
): number {
  if (list && list.length > 0) return SEVERITY_ORDER.indexOf(worstException(list).severity)
  if (covered && covered.length > 0) return SEVERITY_ORDER.length
  return SEVERITY_ORDER.length + 1
}

/** All covering rows per route instance id: a double-manned route has one row per filled seat. */
export function buildCoveredIndex(
  covered: CoveredRoute[] | undefined,
): Map<string, CoveredRoute[]> {
  const map = new Map<string, CoveredRoute[]>()
  for (const row of covered ?? []) {
    map.set(row.routeInstanceId, [...(map.get(row.routeInstanceId) ?? []), row])
  }
  return map
}

/** Exceptions grouped by route; orphan shifts group by shift instead. */
export function groupByRoute(rows: ShiftException[]): ShiftException[][] {
  const groups = new Map<string, ShiftException[]>()
  for (const r of rows) {
    const key = r.routeInstanceId ?? `shift:${r.shiftId}`
    const list = groups.get(key) ?? []
    list.push(r)
    groups.set(key, list)
  }
  return [...groups.values()]
}

/** Counted by route (or orphan shift), never by finding, so badges agree with the panel's cards. */
export function countRoutesBySeverity(
  rows: ShiftException[] | undefined,
): Record<ShiftExceptionSeverity, number> {
  const counts: Record<ShiftExceptionSeverity, number> = { unstaffed: 0, drift: 0, orphan: 0 }
  for (const severity of SEVERITY_ORDER) {
    counts[severity] = groupByRoute((rows ?? []).filter((r) => r.severity === severity)).length
  }
  return counts
}

/** Red counts uncovered routes; amber counts drift only when nothing is uncovered; orphans never raise it. */
export function coverBadge(
  rows: ShiftException[] | undefined,
): { count: number; tone: Tone } | null {
  const counts = countRoutesBySeverity(rows)
  if (counts.unstaffed > 0) return { count: counts.unstaffed, tone: "danger" }
  if (counts.drift > 0) return { count: counts.drift, tone: "warning" }
  return null
}

/** "15:30–01:00 +1": the day offset matters, since overnight vs same-day ends read identically otherwise. */
export function formatWindow(start: string | null, end: string | null): string | null {
  if (!start) return null
  const from = format(new Date(start), "HH:mm")
  if (!end) return from
  const to = format(new Date(end), "HH:mm")
  const days = differenceInCalendarDays(startOfDay(new Date(end)), startOfDay(new Date(start)))
  if (days > 0) return `${from}\u2013${to} +${days}`
  if (days < 0) return `${from}\u2013${to} ${days}`
  return `${from}\u2013${to}`
}

/** Which shift values disagree (coloured in place), plus the findings with no value to colour, in words. */
export function describeCover(rows: ShiftException[]): {
  timeDiffers: boolean
  driverDiffers: boolean
  words: string[]
} {
  const types = new Set(rows.map((r) => r.exceptionType))
  const words: string[] = []
  if (types.has("vehicle_mismatch")) words.push("different vehicle")
  if (types.has("vehicle_class_mismatch")) words.push("wrong vehicle class")
  if (types.has("depot_mismatch")) words.push("booked at another depot")
  if (types.has("duplicate_shift")) {
    const n = new Set(rows.filter((r) => r.exceptionType === "duplicate_shift").map((r) => r.shiftId)).size
    words.push(`${n} shifts carry this code`)
  }
  return {
    timeDiffers: types.has("start_time_mismatch") || types.has("end_time_mismatch"),
    driverDiffers: types.has("driver_mismatch"),
    words,
  }
}

/** Strips the "[COL]" prefix and the date; keeps the run number, which separates same-day runs. */
export function displayRouteCode(code: string | null): { code: string; run: number | null } {
  if (!code) return { code: "", run: null }
  const stripped = code.replace(/^\s*\[?\s*COL\s*\]?\s*[-_:]?\s*/i, "")
  const m = stripped.match(/^(.*?)[-_\s]*\b20\d{6}\b(?:[-_\s]*(\d+))?\s*$/)
  if (!m) return { code: stripped, run: null }
  return { code: m[1], run: m[2] ? Number(m[2]) : null }
}

// The backend's rule, so depot filters agree with the shift check.
export { depotsAgree } from "../../supabase/functions/_shared/shiftMatching.ts"

/** "HIVED Silvertown Depot" and "Silvertown Depot" both read as Silvertown. */
export function displayDepot(depot: string | null): string | null {
  if (!depot) return null
  return depot.replace(/\bHIVED\b/gi, "").replace(/\bDepot\b/gi, "").replace(/\s+/g, " ").trim() || depot
}

/** Drops the resourcing id prefix ("33420 - Name"). */
export function displayPerson(name: string | null | undefined): string | null {
  if (!name) return null
  return name.replace(/^\s*\d+\s*[-\u2013:]\s*/, "").trim() || name
}

export function coverTitle(list: ShiftException[]): string {
  return list.map((c) => c.detail).join("\n")
}

/** Exceptions by route instance id; orphan shifts (no route) are excluded. */
export function buildCoverIndex(exceptions: ShiftException[] | undefined): Map<string, ShiftException[]> {
  const index = new Map<string, ShiftException[]>()
  for (const exception of exceptions ?? []) {
    if (!exception.routeInstanceId) continue
    const list = index.get(exception.routeInstanceId) ?? []
    list.push(exception)
    index.set(exception.routeInstanceId, list)
  }
  return index
}
